import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail, Utensils } from "lucide-react";
import { Button } from "@/components/ui/button";
import ReviewSection from "@/components/ReviewSection";

const CATEGORY_IMAGES = {
  starters: "https://images.unsplash.com/photo-1772457677598-2dc68bb6f4c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBhcHBldGl6ZXJzJTIwc2Ftb3NhJTIwcGxhdHRlcnxlbnwwfHx8fDE3NzI2ODM5MDV8MA&ixlib=rb-4.1.0&q=85&w=600",
  main_course: "https://images.unsplash.com/photo-1672477179695-7276b0602fa9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBmZWFzdCUyMHRoYWxpJTIwdG9wJTIwdmlldyUyMHJpY2glMjBjb2xvcnN8ZW58MHx8fHwxNzcyNjgzOTEzfDA&ixlib=rb-4.1.0&q=85&w=600",
  rice_bread: "https://images.unsplash.com/photo-1589302168068-964664d93dc0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxjaGlja2VuJTIwYmlyeWFuaSUyMEluZGlhbiUyMHJpY2V8ZW58MHx8fHwxNzc0MTM4MzU2fDA&ixlib=rb-4.1.0&q=85&w=600",
  desserts: "https://customer-assets.emergentagent.com/job_menu-select-3/artifacts/ot82zhq6_IMG_7326.webp",
};

const CATEGORIES = [
  { key: "starters", label: "Starters", desc: "Samosas, Tikkas & more" },
  { key: "main_course", label: "Main Course", desc: "Curries, Dal & Paneer" },
  { key: "rice_bread", label: "Rice & Bread", desc: "Biryani, Pulao & Roti" },
  { key: "desserts", label: "Desserts", desc: "Halwa, Kheer & sweets" },
];

export default function HomePage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_menu-select-3/artifacts/7p083xbw_IMG_7325.jpeg)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#78350F]/90 via-[#78350F]/70 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <div className="max-w-2xl animate-fade-up">
            <p className="font-body text-[#FED7AA] text-xs sm:text-sm tracking-[0.2em] uppercase mb-4">
              Traditional Indian Catering
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-none mb-6">
              Bringing the flavors of India to your doorstep
            </h1>
            <p className="font-body text-base sm:text-lg text-[#FED7AA]/90 leading-relaxed mb-8 max-w-lg">
              The perfect catering solution for your next home party, dinner, or
              get-together. Authentic homemade taste in family packs.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/menu">
                <Button
                  data-testid="hero-browse-menu"
                  className="rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-body font-semibold px-8 py-3 h-12 text-base shadow-lg hover:shadow-xl transition-all active:scale-95"
                >
                  Browse Menu
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="tel:6092880605">
                <Button
                  data-testid="hero-call-btn"
                  variant="outline"
                  className="rounded-full border-2 border-white/30 bg-transparent text-white hover:bg-white/10 font-body font-semibold px-8 py-3 h-12 text-base transition-all"
                >
                  <Phone className="mr-2 h-4 w-4" />
                  Call Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Tray Info Strip */}
      <section className="bg-[#78350F] py-4" data-testid="tray-info-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-6 sm:gap-12 text-center">
            {[
              { size: "Small Tray", serves: "~10 people" },
              { size: "Medium Tray", serves: "~20 people" },
              { size: "Med-Large Tray", serves: "~25-30 people" },
              { size: "Large Tray", serves: "~35-40 people" },
            ].map((t) => (
              <div key={t.size} className="flex items-center gap-2">
                <Utensils className="h-4 w-4 text-[#FED7AA]" />
                <span className="font-body text-white text-sm font-semibold">
                  {t.size}
                </span>
                <span className="font-body text-[#FED7AA] text-sm">
                  serves {t.serves}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 sm:py-20" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12 animate-fade-up">
            <p className="font-body text-xs text-[#EA580C] tracking-[0.2em] uppercase mb-3 font-semibold">
              About M.O.M
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#78350F] mb-4">
              Home-Style Indian Cuisine
            </h2>
            <p className="font-body text-stone-500 leading-relaxed">
              We serve traditional Indian cuisine for home parties, dinners, and
              any get-togethers, with food delivered in family packs of 10, 20,
              and 35 sizes. Made with authentic Indian ingredients and spices.
            </p>
          </div>

          {/* Category Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {CATEGORIES.map((cat, i) => (
              <Link
                to={`/menu?category=${cat.key}`}
                key={cat.key}
                data-testid={`category-card-${cat.key}`}
                className={`relative overflow-hidden rounded-2xl aspect-[4/5] group cursor-pointer animate-fade-up delay-${
                  (i + 1) * 100
                }`}
              >
                <img
                  src={CATEGORY_IMAGES[cat.key]}
                  alt={cat.label}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                  <h3 className="font-heading text-lg sm:text-xl font-semibold text-white mb-1">
                    {cat.label}
                  </h3>
                  <p className="font-body text-xs text-white/70">{cat.desc}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Link to="/menu">
              <Button
                data-testid="view-full-menu-btn"
                className="rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-body font-semibold px-10 py-3 h-12 text-base shadow-lg hover:shadow-xl transition-all active:scale-95"
              >
                View Full Menu
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewSection />

      {/* Contact Strip */}
      <section
        className="bg-gradient-to-br from-orange-50 to-amber-100 py-12"
        data-testid="contact-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[#78350F] mb-6">
            Ready to place an order?
          </h2>
          <div className="flex flex-wrap justify-center gap-6">
            <a
              href="tel:6092880605"
              data-testid="contact-phone-1"
              className="flex items-center gap-2 font-body text-stone-600 hover:text-[#EA580C] transition-colors"
            >
              <Phone className="h-4 w-4" />
              (609)-288-0605
            </a>
            <a
              href="tel:6099337076"
              data-testid="contact-phone-2"
              className="flex items-center gap-2 font-body text-stone-600 hover:text-[#EA580C] transition-colors"
            >
              <Phone className="h-4 w-4" />
              (609)-933-7076
            </a>
            <a
              href="mailto:mealsomeals@gmail.com"
              data-testid="contact-email"
              className="flex items-center gap-2 font-body text-stone-600 hover:text-[#EA580C] transition-colors"
            >
              <Mail className="h-4 w-4" />
              mealsomeals@gmail.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
